import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, AlertCircle, Loader2 } from "lucide-react";

/** Phase 55 — Employer-side card with:
 *   - Current-month AI spend / remaining quota
 *   - Toggle: auto-parse resumes (company default)
 *   - Toggle: auto-parse override (per-recruiter)
 *   - "Request more quota" button (creates a topup admin needs to approve)
 *
 * Drop this into CompanyPage so employers/recruiters self-serve their AI cost
 * settings instead of needing admin access.
 */
export default function CompanyAISettingsCard() {
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [topupAmt, setTopupAmt] = useState(10);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ai-budget/me");
            setSnap(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const flipCompany = async (val) => {
        setBusy(true);
        try { await api.put("/company/auto-ai-parsing", { enabled: val }); toast.success(val ? "Auto-parse ON" : "Auto-parse OFF"); await refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const flipMe = async (val) => {
        setBusy(true);
        try { await api.put("/me/auto-ai-parsing", { enabled: val }); toast.success("Saved"); await refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const requestTopup = async () => {
        if (!topupAmt || topupAmt <= 0) return;
        setBusy(true);
        try {
            await api.post("/ai-budget/topup", { requested_amount_usd: Number(topupAmt), note: "From company settings" });
            toast.success(`Topup request for $${topupAmt} submitted — admin will approve.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    if (loading) return <Card className="p-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading AI cost…</Card>;
    if (!snap) return null;

    const exhausted = snap.remaining_usd <= 0;
    const userOverride = snap.recruiter_auto_ai_parsing_override;  // null | true | false
    const recruiterOverrideMode = userOverride === null || userOverride === undefined ? "" : (userOverride ? "on" : "off");

    return (
        <Card className="p-5 space-y-4" data-testid="company-ai-settings">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-black text-lg flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-ikb" /> AI cost & auto-parsing
                    </h3>
                    <p className="text-xs text-foreground/60 mt-1 max-w-xl">
                        Turn off automatic AI parsing to conserve credit when bulk-uploading. Recruiters can still run AI on demand from any candidate row.
                    </p>
                </div>
            </div>

            {/* Spend snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Mini label={`Spent (${snap.month})`} value={`$${snap.spent_usd.toFixed(2)}`} />
                <Mini label="Monthly limit" value={`$${snap.monthly_limit_usd.toFixed(2)}`} />
                <Mini label="Extra quota" value={`$${snap.extra_quota_usd.toFixed(2)}`} />
                <Mini label="Remaining" value={`$${snap.remaining_usd.toFixed(2)}`} highlight={exhausted ? "rose" : "emerald"} />
            </div>

            {exhausted && (
                <div className="border border-rose-300 bg-rose-50 text-rose-700 text-xs p-3 flex items-start gap-2" data-testid="ai-budget-exhausted">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                        AI quota exhausted for this month. Auto-parse and AI scoring are paused. Request more quota below or wait until next month.
                    </div>
                </div>
            )}

            {/* Toggles */}
            <div className="border-t border-border pt-3 space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!snap.auto_ai_parsing_enabled}
                        onChange={(e) => flipCompany(e.target.checked)}
                        disabled={busy}
                        className="w-4 h-4"
                        data-testid="company-auto-ai-parsing-toggle"
                    />
                    <div>
                        <div className="text-sm font-semibold">Auto-parse new resumes with AI (company-wide)</div>
                        <div className="text-[11px] text-foreground/60">When OFF, resumes are stored but AI parse/scoring waits until a recruiter clicks &ldquo;Run AI&rdquo; on the candidate row.</div>
                    </div>
                </label>

                <div className="pl-7">
                    <label className="text-[11px] font-mono uppercase tracking-widest text-foreground/60">My recruiter override</label>
                    <select
                        value={recruiterOverrideMode}
                        onChange={(e) => flipMe(e.target.value === "" ? null : e.target.value === "on")}
                        disabled={busy}
                        className="block mt-1 rounded-none border border-border bg-white px-2 py-1 text-xs font-mono"
                        data-testid="recruiter-auto-ai-override"
                    >
                        <option value="">Use company default ({snap.auto_ai_parsing_enabled ? "ON" : "OFF"})</option>
                        <option value="on">Always ON for me</option>
                        <option value="off">Always OFF for me</option>
                    </select>
                </div>
            </div>

            {/* Topup */}
            <div className="border-t border-border pt-3">
                <div className="text-xs font-mono uppercase tracking-widest text-foreground/60 mb-2">Request additional quota</div>
                <div className="flex items-center gap-2">
                    <span className="text-sm">$</span>
                    <Input
                        type="number"
                        min={1}
                        value={topupAmt}
                        onChange={(e) => setTopupAmt(e.target.value)}
                        className="rounded-none h-9 w-28 font-mono"
                        data-testid="ai-topup-amount"
                    />
                    <Button onClick={requestTopup} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 text-white" data-testid="ai-topup-request">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                        Request topup
                    </Button>
                </div>
                <p className="text-[10px] text-foreground/50 mt-2">Admin reviews + approves topup requests. Credit is added to your &ldquo;Extra quota&rdquo; line above.</p>
            </div>
        </Card>
    );
}

function Mini({ label, value, highlight }) {
    const colorCls = highlight === "rose" ? "text-rose-600" : highlight === "emerald" ? "text-emerald-700" : "";
    return (
        <div className="border border-border bg-white p-2 text-center">
            <div className="text-[9px] font-mono uppercase tracking-widest text-foreground/50">{label}</div>
            <div className={`font-display font-black text-base mt-0.5 ${colorCls}`}>{value}</div>
        </div>
    );
}
